import { useExternalScript } from "src/hooks/useExternalScript";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { addZero } from "src/util/textUtil";
import { TimeStampType } from "src/interface/TimeStamp";
import {
  DAY_OF_WEEKS,
  ArgStatusType,
  ARG_STATUS,
  ERROR_TEXT,
  PATH,
  APP_AREA,
  SAFIE_STORAGE_KEYS,
} from "src/const";
import { useHistory, useLocation } from "react-router-dom";
import { convertPlayTimestampToTimeStamp } from "src/util/convertPlayTimestampToTimeStamp";

const HEIGHT_WITHOUT_VIDEO =
  APP_AREA.HEADER_HEIGHT +
  APP_AREA.VIDEO_CONTROL_BAR_HEIGHT +
  APP_AREA.TIMELINE_HEIGHT +
  APP_AREA.TIMEBOX_HEIGHT;

export const useStreaming = (
  videoRef: React.RefObject<HTMLDivElement> | null,
  deviceId: string | null,
  safieApiKey: { status: ArgStatusType; data: string | undefined }
) => {
  const history = useHistory();
  const location = useLocation<{ device?: any }>();
  const isViewOnFrame = window !== window.parent;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [selectedTimestamp, setSelectedTimestamp] = useState<
    TimeStampType | undefined
  >(undefined);

  const [playTimestamp, setPlayTimestamp] = useState<TimeStampType | undefined>(
    undefined
  );
  const [playerItem, setPlayerItem] = useState<
    { player: any; node: HTMLDivElement } | undefined
  >(undefined);
  const [device, setDevice] = useState<
    | {
        deviceId: string;
        setting: { name: string };
        status: { videoStreaming: boolean };
      }
    | undefined
  >(undefined);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const isScriptLoaded = useExternalScript(
    "https://safie.link/sdk/js/api/v1/latest/"
  );
  const win: any = window;

  const playerRef = useRef(null);
  const deviceSelectRef = useRef(null);

  /** ストリーミング再生のモードがliveかどうか */
  const isLive = playerItem?.player.streamingMode === "live";
  /** ストリーミング再生のモードが再生していないでないかどうか */
  const isNotPlaying = playerItem?.player.streamingMode === "none";

  /** 音量 */
  const volume = playerItem?.player.volume;

  /** ミュート */
  const muted = playerItem?.player.muted;

  const getUserInfo = useCallback(async () => {
    try {
      await win.Safie.Users.queryInformation();
    } catch (error) {
      console.error("userInfo error", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // windowは不変のため依存配列は空でOK

  const initialize = useCallback(async () => {
    // tokenの設定とユーザ情報の取得
    try {
      const { tokenType } = await win.Safie.Auth.initialize();

      if (tokenType === win.Safie.Auth.TokenType.AccessToken) {
        await getUserInfo();
      }
    } catch (error: any) {
      console.error("initialize error", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserInfo]); // windowは不変のため依存配列から除外

  const getDevices = useCallback(() => {
    win.Safie.Devices.queryDevices({ limit: 100 })
      .then((devices: any) => {
        if (devices.list.length === 0) {
          // カメラが一つもなければ、エラーを返す
          setErrorText(ERROR_TEXT.DISCOLLABORATE_SAFIE_CAMERA);
          setIsLoading(false);
          return;
        }
        if (!deviceId) {
          return;
        }
        const shopDevice = devices.list.find(
          (device: {
            deviceId: string;
            setting: { name: string };
            status: { videoStreaming: boolean };
          }) => device.deviceId === deviceId
        );
        if (!shopDevice) {
          alert(ERROR_TEXT.FILTER_SAFIE_CAMERA_IS_NONE_ON_STREAM);
          history.push(PATH.CAMERA_DEVICES);
          return;
        }
        setDevice(shopDevice ?? undefined);
      })
      .catch((error: any) => {
        console.error("getDevices error", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, history]); // windowは不変のため依存配列から除外

  const setToken = useCallback(async () => {
    // グローバルフラグで二重setTokenを防ぐ（同一ページ内での二重実行を回避）
    // window.safieTokenSet はページリロード時にクリアされるため、再訪時も正常に動作
    const tokenAlreadySetInPage = window.safieTokenSet;

    // iframe環境ではsessionStorageフラグを使わない
    const tokenAlreadySetInSession = isViewOnFrame ? null : sessionStorage.getItem(SAFIE_STORAGE_KEYS.TOKEN_SET);

    // 既にトークンが設定済みの場合はスキップ
    if (tokenAlreadySetInPage || tokenAlreadySetInSession) {
      return true;
    }

    // Safie SDKは setToken() で既存トークンを上書きできる
    // removeToken() を呼ぶとSDK内部状態が壊れるため呼ばない

    if (
      safieApiKey.status !== ARG_STATUS.SUCCESS &&
      safieApiKey.status !== ARG_STATUS.ASSIGN
    )
      return false;
    const token = safieApiKey.data;
    if (token == null || token === "") {
      return false;
    }

    // Safie SDKが利用可能かチェック
    if (!win.Safie || !win.Safie.Auth || !win.Safie.Auth.setToken) {
      console.error("[streaming] Safie SDK not available");
      return false;
    }

    try {
      const setTokenPromise = win.Safie.Auth.setToken(token, "apiKey");

      await Promise.race([
        setTokenPromise,
        new Promise((_, reject) =>
          setTimeout(() => {
            reject(new Error('setToken timeout after 10 seconds'));
          }, 10000)
        )
      ]);

      // グローバルフラグとsessionStorageフラグを設定
      window.safieTokenSet = true;
      if (!isViewOnFrame) {
        sessionStorage.setItem(SAFIE_STORAGE_KEYS.TOKEN_SET, 'true');
      }
      return true;
    } catch (error: any) {
      console.error("[streaming] setToken error:", error?.message);

      // タイムアウトエラーの場合、API リクエストは成功している可能性が高い
      if (error?.message?.includes('timeout')) {
        console.warn("[streaming] Timeout, treating as potential success");
        // グローバルフラグとsessionStorageフラグを設定
        window.safieTokenSet = true;
        if (!isViewOnFrame) {
          sessionStorage.setItem(SAFIE_STORAGE_KEYS.TOKEN_SET, 'true');
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        return true;
      }

      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safieApiKey]); // windowは不変のため依存配列から除外

  const removeToken = async () => {
    await win.Safie.Auth.removeToken();
  };

  const addPlayer = useCallback(async () => {
    let player: any = null;
    try {
      if (!device) {
        return;
      }
      if (playerItem) {
        playerItem.player.dispose();
        playerItem.node.remove();
        setPlayerItem(undefined);
      }
      // 動画ノード
      const videoNode = document.createElement("div");
      videoNode.style.width = "100%";
      videoNode.style.height = `calc(100vh - ${HEIGHT_WITHOUT_VIDEO}px)`;
      videoNode.style.aspectRatio = "16/9";
      if (videoRef && videoRef.current) {
        videoRef.current.append(videoNode);
      }
      if (videoRef && videoRef.current) {
        player = new win.Safie.Player.StreamingPlayer(videoNode);
        const onTimeChange = (timestamp: number) => {
          let dateTime = new Date(timestamp);
          setPlayTimestamp({
            year: dateTime.getFullYear(),
            month: dateTime.getMonth() + 1,
            date: dateTime.getDate(),
            dayOfWeek: DAY_OF_WEEKS[dateTime.getDay()],
            hour: dateTime.getHours(),
            minute: dateTime.getMinutes(),
            second: dateTime.getSeconds(),
          });
        };
        player.on("playTimeChange", onTimeChange);
        setPlayerItem({ player, node: videoNode });
        player.deviceId = device.deviceId;
        player.volume = 0;
        player.muted = true;
        if (device.status.videoStreaming) {
          player.play();
          // 再生出来る状態のため、errorTextを消す
          setErrorText(undefined);
        }
      }
    } catch (error: any) {
      alert(`エラーtype: ${error.type} message: ${error.message}`);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, videoRef]); // playerItemを依存配列から除外して無限ループを防ぐ

  const streamTimestamp = useMemo(() => {
    if (!selectedTimestamp) return "";
    return `${selectedTimestamp.year}-${addZero(
      selectedTimestamp.month
    )}-${addZero(selectedTimestamp.date)}T${addZero(
      selectedTimestamp.hour
    )}:${addZero(selectedTimestamp.minute)}:00`;
  }, [selectedTimestamp]);

  /** Live配信に切り替えるハンドラ */
  const handleLiveStreaming = useCallback(() => {
    if (!playerItem) return;
    playerItem.player.play();
  }, [playerItem]);

  const handlePause = useCallback(() => {
    if (!playerItem) return;
    playerItem.player.pause();
  }, [playerItem]);

  const handleUnpause = useCallback(() => {
    if (!playerItem) return;
    playerItem.player.unpause();
  }, [playerItem]);

  const handleChangeVolume = useCallback(
    (volume: number) => {
      if (!playerItem) return;
      playerItem.player.volume = volume;
      if (volume === 0) {
        playerItem.player.muted = true;
      } else {
        playerItem.player.muted = false;
      }
    },
    [playerItem]
  );

  const onStatusChange = (context: { status: string }) => {
    if (context.status === "paused") {
      setIsPaused(true);
    }
    if (context.status === "streaming") {
      setIsPaused(false);
    }
  };

  useEffect(() => {
    if (!playerItem) return;
    if (!playerItem.player) return;
    playerItem.player.on("statusChange", onStatusChange);
    return () => {
      if (!playerItem) return;
      if (!playerItem.player) return;
      // NOTE: クリーンアップする際に、Cannot read properties of nul (reading 'removeListener')が出るのを防ぐため追加
      //https://safie.link/sdk/js/api/v1/1.2/doc/classes/Player.StreamingPlayer.html#dispose
      // player.dispose()を使ってインスタンスが使用不可になるため、removeListenerが使えなくなるよう
      if (playerItem?.player._emitter) {
        playerItem?.player?.off("statusChange", onStatusChange);
      }
    };
  }, [playerItem]);

  /** 選択時間を操作するハンドラ */
  const handlePlaybackTime = (second: number) => {
    if (!playerItem) return;
    if (!playTimestamp) return;
    const timestamp = convertPlayTimestampToTimeStamp(playTimestamp) ?? 0;
    playerItem.player.play(timestamp + second * 1000);

    // NOTE: タイムラインと同じように停止中は,playTimeChangeイベントが発火しないため、ここで時間を更新する
    const date = new Date(timestamp + second * 1000);
    if (isPaused && isNotPlaying) {
      setPlayTimestamp({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        date: date.getDate(),
        dayOfWeek: DAY_OF_WEEKS[date.getDay()],
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
      });
    }
  };

  // TODO:動画の時間変更
  useEffect(() => {
    if (!playerItem) return;
    if (!selectedTimestamp) return;

    const dateString = `${selectedTimestamp.year}-${addZero(
      selectedTimestamp.month
    )}-${addZero(selectedTimestamp.date)}T${addZero(
      selectedTimestamp.hour
    )}:${addZero(selectedTimestamp.minute)}:${addZero(
      selectedTimestamp.second
    )}`;
    const selectedData = new Date(dateString);

    // 現在時間以上かどうか
    const now = new Date();
    if (now > selectedData) {
      playerItem.player.play(selectedData.getTime());
    } else {
      playerItem.player.play();
    }
  }, [playerItem, selectedTimestamp]);

  useEffect(() => {
    if (!isScriptLoaded) return;
    if (!safieApiKey) return;

    // APIキーの状態をチェック
    if (safieApiKey.status !== ARG_STATUS.SUCCESS && safieApiKey.status !== ARG_STATUS.ASSIGN) {
      return;
    }
    (async () => {
      const result = await setToken();
      if (!result) {
        console.error("tokenの取得に失敗しました");
        setErrorText(ERROR_TEXT.API_KEY_ERROR);
        setIsLoading(false);
        return;
      }

      // NOTE: カメラデバイス一覧画面で既にinitialize()が呼ばれているため、
      // Streaming画面では二重初期化を避けてスキップする
      // 二重にwin.Safie.Auth.initialize()を呼ぶとSDK内部でハングする

      // グローバルフラグで二重初期化を防ぐ（同一ページ内での二重実行を回避）
      // window.safieInitialized はページリロード時にクリアされるため、再訪時も正常に動作
      const alreadyInitializedInPage = window.safieInitialized;

      // sessionStorageフラグ（iframe環境では使わない）
      const alreadyInitializedInSession = isViewOnFrame ? null : sessionStorage.getItem(SAFIE_STORAGE_KEYS.INITIALIZED);

      if (!alreadyInitializedInPage && !alreadyInitializedInSession) {
        await initialize();
        window.safieInitialized = true;
        // iframe環境ではsessionStorageフラグを設定しない
        if (!isViewOnFrame) {
          sessionStorage.setItem(SAFIE_STORAGE_KEYS.INITIALIZED, 'true');
        }
      }

      // location.stateからデバイス情報が渡されている場合は、queryDevices()をスキップ
      const deviceFromState = location.state?.device;
      if (deviceFromState && deviceFromState.deviceId === deviceId) {
        setDevice(deviceFromState);
      } else {
        // NOTE: SDK内部の状態準備のため少し待つ
        setTimeout(() => {
          getDevices();
        }, 500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded, safieApiKey.status, safieApiKey.data, location.key, location.state, deviceId, getDevices]); // statusとdataを個別に監視、location.keyでページ遷移を検知

  useEffect(() => {
    if (!device) return;
    if (playerItem) return; // 既にプレーヤーが存在する場合はスキップ
    setIsLoading(false);
    addPlayer();
  }, [device, playerItem, addPlayer]);

  return {
    isScriptLoaded,
    playerRef,
    deviceSelectRef,
    addPlayer,
    setToken,
    removeToken,
    getDevices,
    device,
    isLoading,
    selectedTimestamp,
    setSelectedTimestamp,
    isLive,
    handleLiveStreaming,
    streamTimestamp,
    playTimestamp,
    errorText,
    isPaused,
    isNotPlaying,
    handlePause,
    handleUnpause,
    setPlayTimestamp,
    handlePlaybackTime,
    handleChangeVolume,
    volume,
    muted,
  };
};
